import { useConfig } from '@/composables/game/config'
import { computed, ref } from 'vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_TAG,
  MYSTERY_BOX_REWARD_TYPE,
  MYSTERY_BOX_TYPE,
  type MysteryBoxReward,
} from '@/types/shared/player-config'
import { usePlayer } from '@/composables/game/player'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import type { MysteryBoxItem } from '@/types/mystery-box'
import DefaultImage from '@/assets/images/gifs/mystery-box.gif'
import { useEcommerceItems } from '@/composables/game/ecommerce'
import { INVENTORY_ID } from '@/types/shared/player'

export const useMysteryBoxes = () => {
  const { config } = useConfig()
  const { player } = usePlayer()
  const { ecommerceItems } = useEcommerceItems()

  const mysteryBoxRewards = computed(() => config.value.game.ecommerce.mysteryBoxRewards)
  const mysteryBoxEcommerceItems = computed(() => ecommerceItems.value.filter(item => item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX)))
  const unopenedMysteryBoxes = computed(() => player.value.ecommerce.mysteryBoxes)
  const availableMysteryBoxTypes = computed(() => ecommerceItems.value.reduce<MYSTERY_BOX_TYPE[]>((acc, item) => {
    const firstBenefit = item.benefits[0]
    if (!firstBenefit || firstBenefit.type !== ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX) return acc
    if (acc.includes(firstBenefit.boxType)) return acc
    return [...acc, firstBenefit.boxType]
  }, []))

  const rewardTypeOrder = [
    MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT, // All except VIRTUAL_FRIEND
    MYSTERY_BOX_REWARD_TYPE.GIVEAWAY_TICKET,
    MYSTERY_BOX_REWARD_TYPE.SKIN,
  ]

  const benefitTypeOrder = [
    ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN,
    ECOMMERCE_ITEM_BENEFIT_TYPE.COIN,
    ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN,
    ECOMMERCE_ITEM_BENEFIT_TYPE.PROFIT_X2,
    ECOMMERCE_ITEM_BENEFIT_TYPE.OFFLINE_PROFIT,
    ECOMMERCE_ITEM_BENEFIT_TYPE.VIRTUAL_FRIEND,
  ]

  const _mysteryBoxes = computed((): MysteryBoxItem[] => {
    return availableMysteryBoxTypes.value.map(boxType => {
      const mysteryBoxType = boxType as MYSTERY_BOX_TYPE
      const ecommerceItems = getEcommerceItemsForMysteryBox(mysteryBoxType)
      const firstItem = ecommerceItems[0]
      if (!firstItem) throw new Error('No ecommerce item found for the box type ' + mysteryBoxType)
      return {
        rewards: mysteryBoxRewards.value[mysteryBoxType],
        boxType: mysteryBoxType,
        name: firstItem.name,
        image: firstItem.imageUrl ?? DefaultImage,
        description: firstItem.description,
        ecommerceItems,
      }
    })
  })

  const canHaveSale = computed(() => _mysteryBoxes.value.some((box) => box.ecommerceItems.some((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT))))

  function getEcommerceItemsForMysteryBox(boxType: MYSTERY_BOX_TYPE) {
    return mysteryBoxEcommerceItems.value.filter(item => !!(item.benefits.find(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX && b.boxType === boxType)))
  }

  const TOP_PRIORITY_ORDER = [
    'winterCrown',
    'primeSigil',
    'rainbowKey',
    'emberElementShard',
    'frostElementShard',
    'voltElementShard',
    'stoneElementShard',
    'platinumPass',
    'diamondMultiplier',
    'goldTgeBoostCard',
    'silverTgeBoostCard',
    'bronzeTgeBoostCard',
  ]

  function getSortPriorityIndex(reward: MysteryBoxReward) {
    if (reward.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT) {
      const benefitType = reward.benefit?.type
      const amount = reward.benefit?.amount

      if (amount === 1 && TOP_PRIORITY_ORDER.includes(benefitType)) {
        const index = TOP_PRIORITY_ORDER.indexOf(benefitType)
        return -3 + index
      }
    }

    const typeIndex = rewardTypeOrder.indexOf(reward.type)

    return 1000 + typeIndex
  }

  function sortMysteryBoxRewards(mysteryBoxItem: MysteryBoxItem) {
    mysteryBoxItem.rewards = mysteryBoxItem.rewards.sort((a, b) => {
      const priorityA = getSortPriorityIndex(a)
      const priorityB = getSortPriorityIndex(b)

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      if (a.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT && b.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT) {
        const benefitIndexA = benefitTypeOrder.indexOf(a.benefit.type)
        const benefitIndexB = benefitTypeOrder.indexOf(b.benefit.type)
        if (benefitIndexA !== benefitIndexB) return benefitIndexA - benefitIndexB
      }

      const amountA = 'amount' in a ? a.amount : ('benefit' in a ? a.benefit?.amount ?? 0 : 0)
      const amountB = 'amount' in b ? b.amount : ('benefit' in b ? b.benefit?.amount ?? 0 : 0)
      return amountA - amountB
    })

    return mysteryBoxItem
  }

  return {
    mysteryBoxRewards,
    unopenedMysteryBoxes,
    availableMysteryBoxTypes,
    sortMysteryBoxRewards,
    _mysteryBoxes,
    canHaveSale,
  }
}

export const useOpenMysteryBox = () => {
  const { notify } = useToaster()
  const { updatePlayer, player } = usePlayer()

  const openingBoxType = ref<null | MYSTERY_BOX_TYPE>(null)
  const showElementMultiplierRevealedOverlay = ref(false)

  async function openBox(boxType: MYSTERY_BOX_TYPE) {
    openingBoxType.value = boxType
    showElementMultiplierRevealedOverlay.value = false
    try {
      const { rewards, playerStats } = await api.openMysteryBox({ boxType })
      setTimeout(() => {
        updatePlayer(playerStats)
        setTimeout(() => {
          try {
            const shardBenefitTypes = [
              ECOMMERCE_ITEM_BENEFIT_TYPE.EMBER_ELEMENT_SHARD,
              ECOMMERCE_ITEM_BENEFIT_TYPE.FROST_ELEMENT_SHARD,
              ECOMMERCE_ITEM_BENEFIT_TYPE.VOLT_ELEMENT_SHARD,
              ECOMMERCE_ITEM_BENEFIT_TYPE.STONE_ELEMENT_SHARD,
            ]
            const containsShardReward = (rewards ?? []).some((r) => r.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT && shardBenefitTypes.includes(r.benefit.type))
            if (containsShardReward) {
              const counts = [
                player.value.inventory?.[INVENTORY_ID.EMBER_ELEMENT_SHARD] ?? 0,
                player.value.inventory?.[INVENTORY_ID.FROST_ELEMENT_SHARD] ?? 0,
                player.value.inventory?.[INVENTORY_ID.VOLT_ELEMENT_SHARD] ?? 0,
                player.value.inventory?.[INVENTORY_ID.STONE_ELEMENT_SHARD] ?? 0,
              ]
              if (counts.length && Math.min(...counts) > 0) {
                showElementMultiplierRevealedOverlay.value = true
              }
            }
          } catch (err) {
            console.error('Element multiplier overlay logic failed', err)
          }
        }, 2000)
      }, 2000)
      return rewards
    } catch (e) {
      notify({ type: 'error', title: 'Could not open, try again!' })
      await api.reportError({
        code: 'could_not_open_mystery_box',
        metadata: { boxType },
        description: 'Could not open the mystery box',
      })
      return null
    } finally {
      openingBoxType.value = null
    }
  }

  return {
    openBox,
    openingBoxType,
    showElementMultiplierRevealedOverlay,
  }
}
